/**
 * «Ijaraga beriladigan maydonlar» — ochiq auksion lotlari ro'yxati
 * (ijara-urmon.uz ko'rinishi va mexanizmi). Holat, hudud va qidiruv bo'yicha
 * saralash; har bir lot kartochkasini bosib batafsil kartaga o'tiladi.

 * Tuzilishi «Elektron yashil olam» bilan bir xil: chap menyu (holat, hudud, filtrlar),
 * tepada bannerlar, kartochkalarda uchastka xaritasi. Ko'rish uchun kirish shart emas; savdoda qatnashish uchun kirish talab etiladi.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { auctionApi } from "@/shared/api/auction";
import { lotStatusLabel } from "@/shared/api/auction";
import type { AuctionLotRow, AuctionStatus } from "@/shared/api/auction";
import { useI18n } from "@/shared/i18n";
import { formatDateTime, formatMoney, formatNumber } from "@/shared/lib/format";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { useResource } from "@/shared/lib/resource";
import { AsyncBlock } from "@/shared/ui/AsyncBlock";
import { Icon } from "@/shared/ui/Icon";

import { LotMiniMap } from "./landing/LotMiniMap";
import { MarketBanners } from "./landing/MarketBanners";
import { SectionPage } from "./SectionPages";

const STATUS_TABS: Array<{ code: "" | AuctionStatus; label: string }> = [
  { code: "", label: "Barcha auksionlar" },
  { code: "active", label: "Faol savdo" },
  { code: "completed", label: "Yakunlangan" },
  { code: "unsuccessful", label: "O'tkazilmagan" },
];

const STATUS_TONE: Record<AuctionStatus, string> = {
  active: "is-active",
  completed: "is-done",
  contract: "is-contract",
  unsuccessful: "is-failed",
};

const PAGE_ROWS = 3;

type Sort = "new" | "cheap" | "dear" | "area" | "end";

function LotCard({ lot }: { lot: AuctionLotRow }) {
  const { locale } = useI18n();
  const done = lot.status === "completed" || lot.status === "contract";
  return (
    <Link
      className="market-card lot-card is-link"
      to={`${SECTION_PATHS.lease}/${encodeURIComponent(lot.lot_number)}`}
    >
      <div className="lot-cover">
        <LotMiniMap lotNumber={lot.lot_number} />
        <span className={`auc-badge lot-status ${STATUS_TONE[lot.status]}`}>{lotStatusLabel(lot.status, lot.status_label)}</span>
        <span className="badge market-type lot-no">№ {lot.lot_number}</span>
      </div>
      <div className="market-body">
        <h3 title={lot.farm_name}>{lot.farm_name || "Davlat o'rmon xo'jaligi"}</h3>
        <p className="auc-card-sub">{lot.region_name || "—"}</p>
        {lot.purpose ? <p className="auc-card-purpose">{lot.purpose}</p> : null}
        <p className="market-price">
          <b className="num">{formatMoney(done ? lot.winner_bid || lot.current_price : lot.current_price, locale)}</b>
          <span>{done ? "yakuniy narx" : "joriy narx"}</span>
        </p>
        <dl className="market-meta lot-meta">
          <div>
            <dt>Maydon</dt>
            <dd className="num">{formatNumber(Number(lot.area_ha), locale, 2)} ga</dd>
          </div>
          <div>
            <dt>Boshlang'ich</dt>
            <dd className="num">{formatMoney(lot.starting_price, locale)}</dd>
          </div>
          <div>
            <dt>
              <Icon name="clock" size={13} />
              <span className="visually-hidden">Tugash vaqti</span>
            </dt>
            <dd>{lot.end_at ? formatDateTime(lot.end_at, locale) : "—"}</dd>
          </div>
        </dl>
      </div>
    </Link>
  );
}

export function AuctionLotsPage() {
  const { locale } = useI18n();
  const [status, setStatus] = useState<"" | AuctionStatus>("");
  const [region, setRegion] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>("new");
  const [areaMin, setAreaMin] = useState("");
  const [areaMax, setAreaMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [pages, setPages] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(4);
  const shown = pages * cols * PAGE_ROWS;

  /* Hamma lotlar bir marta olinadi; holat, hudud va filtrlar brauzerda qo'llanadi (menyudagi sonlar uchun ham). */
  const all = useResource(() => auctionApi.lots(), []);
  const items = all.data?.results ?? [];

  const statusCount = useMemo(() => {
    const counts: Record<string, number> = { "": items.length };
    for (const lot of items) counts[lot.status] = (counts[lot.status] ?? 0) + 1;
    return counts;
  }, [items]);

  const regionOptions = useMemo(() => {
    const seen = new Map<number, { name: string; count: number }>();
    for (const lot of items) {
      if (lot.region == null || !lot.region_name) continue;
      const row = seen.get(lot.region);
      if (row) row.count += 1;
      else seen.set(lot.region, { name: lot.region_name, count: 1 });
    }
    return [...seen]
      .map(([id, row]) => ({ id: String(id), ...row }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [items, locale]);

  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(locale);
    const price = (lot: AuctionLotRow) => Number(lot.current_price) || 0;
    const filtered = items.filter(
      (lot) =>
        (!status || lot.status === status) &&
        (!region || String(lot.region) === region) &&
        (!areaMin || Number(lot.area_ha) >= Number(areaMin)) &&
        (!areaMax || Number(lot.area_ha) <= Number(areaMax)) &&
        (!priceMin || price(lot) >= Number(priceMin)) &&
        (!priceMax || price(lot) <= Number(priceMax)) &&
        (!needle ||
          `${lot.lot_number} ${lot.farm_name} ${lot.purpose} ${lot.region_name}`.toLocaleLowerCase(locale).includes(needle)),
    );
    const sorted = [...filtered];
    if (sort === "cheap") sorted.sort((a, b) => price(a) - price(b));
    else if (sort === "dear") sorted.sort((a, b) => price(b) - price(a));
    else if (sort === "area") sorted.sort((a, b) => Number(b.area_ha) - Number(a.area_ha));
    else if (sort === "end") sorted.sort((a, b) => (a.end_at ?? "").localeCompare(b.end_at ?? ""));
    return sorted;
  }, [items, status, region, areaMin, areaMax, priceMin, priceMax, query, sort, locale]);

  useEffect(() => {
    document.title = "Ijaraga beriladigan maydonlar — Smart Forestry";
  }, []);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => {
      const n = getComputedStyle(el).gridTemplateColumns.split(" ").filter(Boolean).length;
      if (n > 0) setCols(n);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows.length]);

  const dirty = Boolean(region || areaMin || areaMax || priceMin || priceMax);
  const reset = () => {
    setRegion("");
    setAreaMin("");
    setAreaMax("");
    setPriceMin("");
    setPriceMax("");
    setPages(1);
  };
  const range = (label: string, min: string, max: string, setMin: (v: string) => void, setMax: (v: string) => void) => (
    <div className="market-filter">
      <span className="market-filter-label">{label}</span>
      <div className="market-filter-range">
        <input className="input" type="number" min={0} inputMode="decimal" placeholder="dan" aria-label={`${label}: dan`} value={min} onChange={(e) => { setMin(e.target.value); setPages(1); }} />
        <span aria-hidden="true">—</span>
        <input className="input" type="number" min={0} inputMode="decimal" placeholder="gacha" aria-label={`${label}: gacha`} value={max} onChange={(e) => { setMax(e.target.value); setPages(1); }} />
      </div>
    </div>
  );

  return (
    <SectionPage revealKey={`${status}-${region}-${rows.length}-${shown}`}>
      <section className="section section-alt" id="auction-lots" aria-labelledby="auc-title">
        <div className="container">
          <h2 id="auc-title" className="visually-hidden">
            Ijaraga beriladigan maydonlar
          </h2>

          <div className="market-layout">
            <aside className="market-side" aria-label="Menyu va filtrlar">
              <nav className="market-menu" aria-label="Auksion holati">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.code || "all"}
                    type="button"
                    className={`market-menu-link${status === tab.code ? " is-on" : ""}`}
                    aria-pressed={status === tab.code}
                    onClick={() => {
                      setStatus(tab.code);
                      setPages(1);
                    }}
                  >
                    <Icon name={tab.code === "active" ? "activity" : tab.code === "" ? "map" : "check"} size={18} />
                    <span>{tab.label}</span>
                    {statusCount[tab.code] ? <b className="market-menu-count">{statusCount[tab.code]}</b> : null}
                  </button>
                ))}
              </nav>
              <button
                type="button"
                className="btn btn-outline btn-sm market-panel-toggle"
                aria-expanded={panelOpen}
                onClick={() => setPanelOpen((open) => !open)}
              >
                <Icon name="filter" size={16} />
                Hudud va filtrlar
              </button>
              <div className={`market-panels${panelOpen ? " is-open" : ""}`}>
                <section className="market-panel" aria-labelledby="lot-regions">
                  <h2 id="lot-regions">Hududlar</h2>
                  <ul className="market-cats">
                    <li>
                      <button type="button" className={!region ? "is-on" : ""} onClick={() => { setRegion(""); setPages(1); }}>
                        <span>Barcha viloyatlar</span>
                        <small>{items.length}</small>
                      </button>
                    </li>
                    {regionOptions.map((item) => (
                      <li key={item.id}>
                        <button type="button" className={region === item.id ? "is-on" : ""} onClick={() => { setRegion(item.id); setPages(1); }}>
                          <span>{item.name}</span>
                          <small>{item.count}</small>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
                <section className="market-panel" aria-labelledby="lot-filters">
                  <h2 id="lot-filters">Filtrlar</h2>
                  {range("Maydon (ga)", areaMin, areaMax, setAreaMin, setAreaMax)}
                  {range("Joriy narx (so'm)", priceMin, priceMax, setPriceMin, setPriceMax)}
                  {dirty ? (
                    <button type="button" className="btn btn-outline btn-sm btn-block" onClick={reset}>
                      Filtrlarni tozalash
                    </button>
                  ) : null}
                </section>
              </div>
            </aside>

            <div className="market-main">
              <MarketBanners kind="lease" />

              <div className="market-toolbar" data-reveal>
                <label className="market-search">
                  <Icon name="search" size={16} />
                  <span className="visually-hidden">Qidirish</span>
                  <input
                    className="input"
                    type="search"
                    value={query}
                    placeholder="Lot raqami, xo'jalik yoki maqsad…"
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setPages(1);
                    }}
                  />
                </label>
                <label className="market-sort">
                  <span className="visually-hidden">Saralash</span>
                  <select className="input" value={sort} onChange={(event) => setSort(event.target.value as Sort)}>
                    <option value="new">Standart</option>
                    <option value="end">Tugashi yaqinlari avval</option>
                    <option value="cheap">Narx: arzonidan</option>
                    <option value="dear">Narx: qimmatidan</option>
                    <option value="area">Maydon: kattasidan</option>
                  </select>
                </label>
                <span className="market-found">Topildi: {rows.length}</span>
              </div>

              <AsyncBlock
                loading={all.loading}
                error={all.error}
                onRetry={all.reload}
                empty={rows.length === 0}
                emptyHint="Tanlangan shart bo'yicha lot topilmadi."
                rows={4}
              >
                <div className="market-grid" ref={gridRef}>
                  {rows.slice(0, shown).map((lot) => (
                    <LotCard key={lot.id} lot={lot} />
                  ))}
                </div>
              </AsyncBlock>

              {rows.length > shown ? (
                <div className="market-more">
                  <button type="button" className="btn btn-outline" onClick={() => setPages((n) => n + 1)}>
                    Yana ko'rsatish ({rows.length - shown})
                  </button>
                </div>
              ) : null}

              <p className="market-hint">
                <Icon name="lock" size={14} />
                Savdoda qatnashish uchun ID.GOV.UZ (E-IMZO) orqali kirish talab etiladi.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SectionPage>
  );
}
